import { useQuery } from "@tanstack/react-query";
import { Calendar } from "@/components/ui/calendar";
import { Card } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { addMonths, parseISO, startOfDay, isSameMonth } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { useState } from "react";

export function ProjectCalendarOverview() {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const { data: projects } = useQuery({
    queryKey: ["calendar-projects"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .select("name, prep_date, pre_light_date, shoot_dates")
        .eq("status", "active")
        .order("prep_date", { ascending: true });

      if (error) throw error;
      return data || [];
    },
  });

  const dates = projects?.reduce((acc: { date: Date; name: string; type: 'prep' | 'pre-light' | 'shoot' }[], project) => {
    const events: { date: Date; name: string; type: 'prep' | 'pre-light' | 'shoot' }[] = [];

    if (project.prep_date) {
      const prepDate = startOfDay(parseISO(project.prep_date));
      events.push({
        date: prepDate,
        name: `${project.name}`,
        type: 'prep'
      });
    }

    if (project.pre_light_date) {
      const preLightDate = startOfDay(parseISO(project.pre_light_date));
      events.push({
        date: preLightDate,
        name: `${project.name}`,
        type: 'pre-light'
      });
    }

    if (project.shoot_dates && Array.isArray(project.shoot_dates)) {
      project.shoot_dates.forEach((date: string) => {
        const shootDate = startOfDay(parseISO(date));
        events.push({
          date: shootDate,
          name: `${project.name}`,
          type: 'shoot'
        });
      });
    }

    return [...acc, ...events];
  }, []);

  const modifiers = {
    prep: (date: Date) => 
      dates?.some(d => d.type === 'prep' && d.date.getTime() === startOfDay(date).getTime()),
    preLight: (date: Date) => 
      dates?.some(d => d.type === 'pre-light' && d.date.getTime() === startOfDay(date).getTime()),
    shoot: (date: Date) => 
      dates?.some(d => d.type === 'shoot' && d.date.getTime() === startOfDay(date).getTime()),
  };

  const modifiersStyles = {
    prep: {
      color: 'inherit',
      fontWeight: '400'
    },
    preLight: {
      color: 'inherit',
      fontWeight: '400'
    },
    shoot: {
      color: 'inherit',
      fontWeight: '400'
    }
  };

  const filteredEvents = dates?.filter(event => 
    isSameMonth(event.date, currentMonth)
  ).sort((a, b) => a.date.getTime() - b.date.getTime());

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <CalendarIcon className="h-6 w-6 text-primary" />
        <div>
          <h2 className="text-xl font-semibold text-white">Project Calendar</h2>
          <p className="text-sm text-white/60">Schedule overview for active projects</p>
        </div>
      </div>

      <Card className="bg-[#14121F] border-white/10">
        <Calendar
          mode="multiple"
          modifiers={modifiers}
          modifiersStyles={modifiersStyles}
          month={currentMonth}
          onMonthChange={setCurrentMonth}
          className="rounded-md"
          classNames={{
            months: "space-y-4 sm:space-y-5",
            month: "space-y-4",
            caption: "flex justify-between pt-1 relative items-center text-white px-4",
            caption_label: "text-lg font-medium",
            nav: "flex items-center gap-1",
            nav_button: "h-7 w-7 bg-transparent p-0 text-white/70 hover:text-white hover:bg-white/10 rounded-md",
            nav_button_previous: "",
            nav_button_next: "",
            table: "w-full border-collapse space-y-1",
            head_row: "flex",
            head_cell: "text-white/50 rounded-md w-9 font-normal text-[0.8rem]",
            row: "flex w-full mt-2",
            cell: "text-center text-sm relative p-0 rounded-md focus-within:relative focus-within:z-20",
            day: "h-9 w-9 p-0 font-normal text-white/70 aria-selected:opacity-100 hover:bg-white/10 rounded-md relative",
            day_selected: "bg-transparent text-white hover:bg-white/10 focus:bg-white/10",
            day_outside: "text-white/30 opacity-50",
            day_disabled: "text-white/30",
            day_range_middle: "aria-selected:bg-accent aria-selected:text-accent-foreground",
            day_hidden: "invisible",
          }}
          components={{
            DayContent: ({ date }) => {
              const isPrepDay = modifiers.prep(date);
              const isPreLightDay = modifiers.preLight(date);
              const isShootDay = modifiers.shoot(date);
              
              return (
                <div className="relative w-full h-full flex items-center justify-center">
                  <span>{date.getDate()}</span>
                  <div className="absolute bottom-1 flex gap-1">
                    {isPrepDay && <div className="w-1.5 h-1.5 rounded-full bg-[#4B7BF5]" />}
                    {isPreLightDay && <div className="w-1.5 h-1.5 rounded-full bg-[#9B87F5]" />}
                    {isShootDay && <div className="w-1.5 h-1.5 rounded-full bg-[#4CAF50]" />}
                  </div>
                </div>
              );
            },
          }}
        />
      </Card>

      <div className="space-y-4">
        <div className="flex flex-wrap gap-2">
          <div className="rounded-full px-4 py-1 text-sm font-medium" style={{ backgroundColor: '#4B7BF5' }}>
            Prep
          </div>
          <div className="rounded-full px-4 py-1 text-sm font-medium" style={{ backgroundColor: '#9B87F5' }}>
            Pre-Light
          </div>
          <div className="rounded-full px-4 py-1 text-sm font-medium" style={{ backgroundColor: '#4CAF50' }}>
            Shoot
          </div>
        </div>

        <div className="space-y-3">
          <h3 className="text-lg font-medium text-white">Events This Month</h3>
          <div className="space-y-3">
            {filteredEvents?.map((event, index) => (
              <div
                key={`${event.name}-${index}`}
                className="flex items-center gap-2"
              >
                <div 
                  className="rounded-full px-4 py-1 text-sm font-medium" 
                  style={{ 
                    backgroundColor: event.type === 'prep' 
                      ? '#4B7BF5' 
                      : event.type === 'pre-light' 
                      ? '#9B87F5' 
                      : '#4CAF50'
                  }}
                >
                  {event.type === 'prep' 
                    ? 'Prep' 
                    : event.type === 'pre-light' 
                    ? 'Pre-Light' 
                    : 'Shoot'}
                </div>
                <div className="flex flex-col">
                  <span className="text-white font-medium">{event.name}</span>
                  <span className="text-sm text-white/60">
                    {event.date.toLocaleDateString('en-US', { 
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </span>
                </div>
              </div>
            ))}
            {filteredEvents?.length === 0 && (
              <p className="text-white/60 text-sm">No events scheduled for this month</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}